import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import {
  CARE_PLAN_WORK_ACTIVITIES,
  CarePlanPickerGoal,
  CarePlanPickerPlan,
  CarePlanPickerStatus,
  CarePlanWorkActivity,
  CarePlanWorkActivityKey,
  CarePlanWorkRequest,
  CarePlanWorkRequestItem,
  CarePlanWorkRequestPlan,
} from 'src/app/models/ccm/care-plan-work.model';
import {
  CARE_PLAN_WORK_LIMITS,
  emptyCarePlanWork,
  sortRequestPlans,
  validateCarePlanWork,
} from 'src/app/services/ccm/care-plan-work.utils';

/** Interventions listed before "Show all N". */
export const COLLAPSED_INTERVENTIONS = 3;

type PickBox = 'goal' | 'interventions';

/** One item no care plan covers, as the picker shows it under the care plans. */
export interface ActivitySection {
  key: CarePlanWorkActivityKey;
  label: string;
  prompt: string;
  placeholder: string;
}

/** Care coordination and other, in the mockup's words (screen 3). */
export const ACTIVITY_SECTIONS: ReadonlyArray<ActivitySection> = [
  {
    key: 'care_coordination',
    label: 'Care coordination',
    prompt: 'What care coordination did you do? (required)',
    placeholder: 'One line on what you coordinated',
  },
  {
    key: 'other',
    label: 'Other',
    prompt: 'What else did you do? (required)',
    placeholder: 'One line on what else you reviewed or did',
  },
];

/** Ticked boxes of the care plans, as the mockup counts them: a goal with both boxes ticked is two items. */
const boxCount = (plans: CarePlanWorkRequestPlan[]): number =>
  plans.reduce(
    (count, plan) =>
      count + plan.items.reduce((boxes, item) => boxes + (item.goal ? 1 : 0) + (item.interventions ? 1 : 0), 0),
    0
  );

/** Stands in for the form callbacks until Angular registers them. */
const noop = (): void => undefined;

/** Gives each picker on a page its own heading id: the time form can hold several CCM rows. */
let nextPickerId = 0;

/**
 * The items behind one CCM time row (BRD-CLIN-2026-008 FR-3): every Active care plan of the patient as its
 * own section, two boxes per goal — the goal, and its interventions as one item — and a required one-line
 * note for each plan picked from; then Care coordination and Other, for work no care plan covers, each
 * with its own required note. Once the care plans have loaded, those two are there even when the patient
 * has no care plan.
 *
 * A form control: its value is the `care_plan_work` the time form sends for the row, and its validity —
 * `validateCarePlanWork`, plus "not loaded" while `status` is not ready — keeps Save off through the
 * form's own `invalid` (FR-3.4). The value is emitted only when the picks really change, so loading the
 * sections never marks the time form dirty.
 *
 * A box can always be unticked. A goal from the frozen copy of an entry being edited can only get back
 * the boxes it was saved with: the server refuses a new pick on a plan or goal that is gone.
 */
@Component({
  selector: 'app-ccm-care-plan-picker',
  templateUrl: './ccm-care-plan-picker.component.html',
  styleUrls: ['./ccm-care-plan-picker.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CcmCarePlanPickerComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => CcmCarePlanPickerComponent), multi: true },
  ],
})
export class CcmCarePlanPickerComponent implements ControlValueAccessor, Validator, OnChanges {
  /**
   * False for an entry already CCM before go live with no saved picks, which the server lets be edited
   * without care plan work (FR-5.3). Anything picked still needs its note.
   *
   * Not named `required`: a `[required]` binding next to `[formControl]` would also attach Angular's
   * `RequiredValidator`, which reads an empty array as missing and makes "nothing picked" invalid always.
   */
  @Input() requirePicks = true;

  /** Until the sections are loaded the picker says so and keeps its row invalid. */
  @Input() status: CarePlanPickerStatus = 'ready';

  /** Which row this is, e.g. "Time 2", read out with the heading when the time form holds several CCM rows. */
  @Input() rowLabel = '';

  /**
   * Asked for from the "Try again" button when the sections failed to load. Closing the time form instead
   * would drop the timer's minutes.
   */
  @Output() retry = new EventEmitter<void>();

  /**
   * The time form can take the nurse to the Care Plans tab and back with the entry kept (S2.4, FR-4.1):
   * not when the form sits in the timer's leave modal, which has no way back to it.
   */
  @Input() canCreateCarePlan = false;

  /** Asked for from "Create care plan" when there is nothing to pick from. */
  @Output() createCarePlan = new EventEmitter<void>();

  readonly collapsedInterventions = COLLAPSED_INTERVENTIONS;
  readonly activities = ACTIVITY_SECTIONS;
  readonly noteMaxLength = CARE_PLAN_WORK_LIMITS.noteLength;
  readonly titleId = `care-plan-picker-title-${nextPickerId++}`;

  disabled = false;

  private planSections: CarePlanPickerPlan[] = [];
  /** How many problems each plan's goals come from, to group them under headings only when there are several. */
  private problemCounts = new Map<string, number>();

  /** Notes and ticks by plan id; ticks by goal key. A plan is part of the value once one of its boxes is ticked. */
  private notes = new Map<string, string>();
  private ticks = new Map<string, Map<string, CarePlanWorkRequestItem>>();
  private expanded = new Set<string>();
  /**
   * Care coordination and other: which are ticked, and what was typed for each. A note is kept when its
   * item is unticked, so ticking it again brings the text back; only a ticked item is part of the value.
   * That lasts as long as this picker: unticking CCM on the row, or leaving to create a care plan, starts a
   * new one from the value, without the notes of items left unticked.
   */
  private activityTicks = new Set<CarePlanWorkActivityKey>();
  private activityNotes = new Map<CarePlanWorkActivityKey, string>();
  /** The value last written or emitted, to emit only real changes. */
  private lastValueJson = JSON.stringify(emptyCarePlanWork());
  private syncQueued = false;

  private onChange: (value: CarePlanWorkRequest) => void = noop;
  private onTouched: () => void = noop;
  private onValidatorChange: () => void = noop;

  /**
   * What the time form sends for this row: the picked plans, each with its note, in the fixed order the
   * server compares records in, and care coordination and other with theirs. Only goals on screen count:
   * once the sections are ready, a written pick whose goal is not on screen leaves the control, so nothing
   * hidden is sent. `mergeFrozenPicks` puts every stored goal on screen, so that does not happen with what
   * the service loads.
   */
  get value(): CarePlanWorkRequest {
    return {
      plans: this.pickedPlans(),
      care_coordination: this.activityValue('care_coordination'),
      other: this.activityValue('other'),
    };
  }

  /**
   * What is picked, as the mockup words it: "3 items picked across 2 of 3 care plans", "2 items picked on
   * 1 care plan, plus care coordination", or "Care coordination and other" with no care plan item.
   */
  get summary(): string {
    return this.summaryOf(this.value);
  }

  /** The one message the live region reads out: what to do next, or how much is picked. */
  get statusMessage(): string {
    if (this.status === 'loading') {
      return 'Loading the care plans…';
    }
    if (this.status === 'error') {
      return 'The care plans could not be loaded.';
    }
    // The value once, for the check and the summary: this getter runs on every change detection pass.
    const value = this.value;
    const errors = validateCarePlanWork(this.planSections, value, { required: this.requirePicks });
    if (errors?.carePlanTooMany) {
      return `Pick at most ${CARE_PLAN_WORK_LIMITS.plans} care plans and ${CARE_PLAN_WORK_LIMITS.goals} goals for one time entry.`;
    }
    const summary = this.summaryOf(value);
    if (summary) {
      return errors?.carePlanNoteRequired || errors?.carePlanNoteTooLong
        ? summary
        : `${summary}, ${this.noteState(value)}`;
    }
    if (!this.planSections.length) {
      return this.requirePicks
        ? 'This patient has no active care plan. Create the care plan first, or tick Care coordination or Other and say what you did.'
        : 'This patient has no active care plan.';
    }
    if (!this.planSections.some((plan) => plan.goals.length > 0)) {
      return this.requirePicks
        ? 'None of these care plans has an open goal to pick. Add a goal first, or tick Care coordination or Other and say what you did.'
        : '';
    }
    return this.requirePicks
      ? 'Pick at least one goal or interventions box, or tick Care coordination or Other, and write a note for each.'
      : '';
  }

  /** The status line is a warning only when the nurse has something to fix — not while loading, not when all is fine. */
  get statusIsWarning(): boolean {
    if (this.status === 'loading') {
      return false;
    }
    if (this.status === 'error') {
      return true;
    }
    const errors = this.validate();
    return !!errors && !errors.carePlanNoteRequired && !errors.carePlanNoteTooLong;
  }

  /** Offered when the row needs picks and no plan has an open goal to pick, so Save would stay off for good. */
  get showsCreateCarePlan(): boolean {
    return (
      this.status === 'ready' &&
      this.canCreateCarePlan &&
      this.requirePicks &&
      !this.planSections.some((plan) => plan.goals.length > 0)
    );
  }

  get plans(): CarePlanPickerPlan[] {
    return this.planSections;
  }

  @Input()
  set plans(plans: CarePlanPickerPlan[]) {
    this.planSections = plans ?? [];
    this.problemCounts = new Map(
      this.planSections.map((plan) => [plan.carePlanId, new Set(plan.goals.map((goal) => goal.problemId)).size])
    );
    // Picks for goals no longer on screen leave the value, and validity follows the new sections.
    this.syncAfterInputs();
  }

  /** Validity depends on all three inputs; see `syncAfterInputs`. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.requirePicks || changes.status) {
      this.syncAfterInputs();
    }
  }

  writeValue(value: CarePlanWorkRequest | null): void {
    const work = value ?? emptyCarePlanWork();
    this.notes.clear();
    this.ticks.clear();
    this.expanded.clear();
    this.activityTicks.clear();
    this.activityNotes.clear();
    CARE_PLAN_WORK_ACTIVITIES.forEach((key) => {
      if (work[key]) {
        this.activityTicks.add(key);
        this.activityNotes.set(key, work[key].note ?? '');
      }
    });
    (work.plans ?? []).forEach((plan) => {
      this.notes.set(plan.care_plan_id, plan.note ?? '');
      this.ticks.set(
        plan.care_plan_id,
        new Map((plan.items ?? []).map((item) => [this.itemKey(item.problem_id, item.goal_id), { ...item }]))
      );
    });
    // What was written, shaped as `value` shapes it — not `value` itself, which only counts goals on
    // screen: when the sections arrive later, an unchanged pick must not read as a change.
    this.lastValueJson = JSON.stringify({
      plans: sortRequestPlans(
        (work.plans ?? [])
          .map((plan) => ({
            care_plan_id: plan.care_plan_id,
            note: (plan.note ?? '').trim(),
            items: (plan.items ?? [])
              .filter((item) => item.goal || item.interventions)
              .map((item) => this.cleanItem(item)),
          }))
          .filter((plan) => plan.items.length > 0)
      ),
      care_coordination: this.activityValue('care_coordination'),
      other: this.activityValue('other'),
    });
    // Written once the sections are ready: drop, on the next microtask, any pick whose goal is not on screen.
    if (this.status === 'ready') {
      this.syncAfterInputs();
    }
  }

  registerOnChange(fn: (value: CarePlanWorkRequest) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    if (this.status !== 'ready') {
      return { carePlansNotLoaded: true };
    }
    return validateCarePlanWork(this.planSections, this.value, { required: this.requirePicks });
  }

  isTicked(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal, box: PickBox): boolean {
    return this.ticks.get(plan.carePlanId)?.get(this.goalKey(goal))?.[box] === true;
  }

  /**
   * A ticked box can always be unticked. An unticked box can be ticked unless the picker is disabled, the
   * goal has no interventions to pick, or the goal comes from the frozen copy and was not saved with it.
   */
  canChange(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal, box: PickBox): boolean {
    if (this.disabled) {
      return false;
    }
    if (this.isTicked(plan, goal, box)) {
      return true;
    }
    if (goal.frozen) {
      return box === 'goal' ? goal.frozenGoal === true : goal.frozenInterventions === true;
    }
    return box === 'goal' || goal.interventions.length > 0;
  }

  toggle(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal, box: PickBox, checked: boolean): void {
    if (this.disabled || (checked && !this.canChange(plan, goal, box))) {
      return;
    }
    const planTicks = this.ticks.get(plan.carePlanId) ?? new Map<string, CarePlanWorkRequestItem>();
    const key = this.goalKey(goal);
    const item = planTicks.get(key) ?? {
      problem_id: goal.problemId,
      goal_id: goal.goalId,
      goal: false,
      interventions: false,
    };
    planTicks.set(key, { ...item, [box]: checked });
    this.ticks.set(plan.carePlanId, planTicks);
    this.onTouched();
    this.emitIfChanged();
  }

  hasPicks(plan: CarePlanPickerPlan): boolean {
    return this.pickedPlans().some((picked) => picked.care_plan_id === plan.carePlanId);
  }

  noteOf(plan: CarePlanPickerPlan): string {
    return this.notes.get(plan.carePlanId) ?? '';
  }

  onNoteInput(plan: CarePlanPickerPlan, event: Event): void {
    this.notes.set(plan.carePlanId, (event.target as HTMLInputElement).value);
    this.emitIfChanged();
  }

  onNoteBlur(): void {
    this.onTouched();
  }

  /** True when this plan is picked from but its note is still blank. */
  isNoteMissing(plan: CarePlanPickerPlan): boolean {
    return this.hasPicks(plan) && !this.noteOf(plan).trim();
  }

  isActivityTicked(key: CarePlanWorkActivityKey): boolean {
    return this.activityTicks.has(key);
  }

  toggleActivity(key: CarePlanWorkActivityKey, checked: boolean): void {
    if (this.disabled) {
      return;
    }
    if (checked) {
      this.activityTicks.add(key);
    } else {
      this.activityTicks.delete(key);
    }
    this.onTouched();
    this.emitIfChanged();
  }

  activityNoteOf(key: CarePlanWorkActivityKey): string {
    return this.activityNotes.get(key) ?? '';
  }

  onActivityNoteInput(key: CarePlanWorkActivityKey, event: Event): void {
    this.activityNotes.set(key, (event.target as HTMLInputElement).value);
    this.emitIfChanged();
  }

  /** True when the item is ticked but its note is still blank. */
  isActivityNoteMissing(key: CarePlanWorkActivityKey): boolean {
    return this.isActivityTicked(key) && !this.activityNoteOf(key).trim();
  }

  trackActivity(_index: number, activity: ActivitySection): string {
    return activity.key;
  }

  /** Goals are grouped under their problem only when a plan has more than one; most GLB plans have one. */
  showsProblem(plan: CarePlanPickerPlan, index: number): boolean {
    const goals = plan.goals;
    return (
      (this.problemCounts.get(plan.carePlanId) ?? 0) > 1 &&
      (index === 0 || goals[index - 1].problemId !== goals[index].problemId)
    );
  }

  shownInterventions(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal): string[] {
    return this.isExpanded(plan, goal) ? goal.interventions : goal.interventions.slice(0, COLLAPSED_INTERVENTIONS);
  }

  isExpanded(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal): boolean {
    return this.expanded.has(`${plan.carePlanId}/${this.goalKey(goal)}`);
  }

  toggleExpanded(plan: CarePlanPickerPlan, goal: CarePlanPickerGoal): void {
    const key = `${plan.carePlanId}/${this.goalKey(goal)}`;
    if (this.expanded.has(key)) {
      this.expanded.delete(key);
    } else {
      this.expanded.add(key);
    }
  }

  /**
   * The plan's name under its "Care plan:" heading. Plans are often named after the diagnosis plus "Care Plan",
   * which the heading already says, so that ending is dropped instead of reading "Care plan: ... Care Plan".
   */
  planTitle(plan: CarePlanPickerPlan): string {
    return (plan.name ?? '').replace(/\s*care\s*plan\s*$/i, '').trim() || 'Unnamed care plan';
  }

  trackPlan(_index: number, plan: CarePlanPickerPlan): string {
    return plan.carePlanId;
  }

  trackGoal(_index: number, goal: CarePlanPickerGoal): string {
    return `${goal.problemId}/${goal.goalId}`;
  }

  /** Focus goes to the status line first: the button leaves the page as the load starts. */
  onRetry(statusLine: HTMLElement): void {
    statusLine.focus();
    this.retry.emit();
  }

  /**
   * What `value` picks, as the mockup words it: "3 items picked across 2 of 3 care plans", "2 items picked
   * on 1 care plan, plus care coordination", or "Care coordination and other" with no care plan item.
   */
  private summaryOf(value: CarePlanWorkRequest): string {
    const items = boxCount(value.plans);
    const total = this.planSections.length;
    const named = this.activities
      .filter((activity) => value[activity.key])
      .map((activity) => activity.label.toLowerCase())
      .join(' and ');
    const parts: string[] = [];
    if (items) {
      const across = total === 1 ? 'on 1 care plan' : `across ${value.plans.length} of ${total} care plans`;
      parts.push(`${items} ${items === 1 ? 'item' : 'items'} picked ${across}`);
    }
    if (named) {
      parts.push(items ? `plus ${named}` : named.charAt(0).toUpperCase() + named.slice(1));
    }
    return parts.join(', ');
  }

  /** "with a note" or "each with a note", for a value whose notes are all in. */
  private noteState(value: CarePlanWorkRequest): string {
    const noted = value.plans.length + this.activities.filter((activity) => value[activity.key]).length;
    return noted === 1 ? 'with a note' : 'each with a note';
  }

  /** The plans picked from, as `value` holds them. */
  private pickedPlans(): CarePlanWorkRequestPlan[] {
    return sortRequestPlans(
      this.planSections
        .map((plan) => ({
          care_plan_id: plan.carePlanId,
          note: (this.notes.get(plan.carePlanId) ?? '').trim(),
          items: plan.goals
            .map((goal) => this.ticks.get(plan.carePlanId)?.get(this.goalKey(goal)))
            .filter((item): item is CarePlanWorkRequestItem => !!item && (item.goal || item.interventions))
            .map((item) => this.cleanItem(item)),
        }))
        .filter((plan) => plan.items.length > 0)
    );
  }

  /** A ticked item with its note trimmed, `null` when it is not ticked. */
  private activityValue(key: CarePlanWorkActivityKey): CarePlanWorkActivity | null {
    return this.activityTicks.has(key) ? { note: this.activityNoteOf(key).trim() } : null;
  }

  /** A fresh copy with the request's four fields only. */
  private cleanItem(item: CarePlanWorkRequestItem): CarePlanWorkRequestItem {
    return {
      problem_id: item.problem_id,
      goal_id: item.goal_id,
      goal: item.goal === true,
      interventions: item.interventions === true,
    };
  }

  /**
   * Re-emits and re-validates after an input changed — on the next microtask, out of change detection.
   * Inputs reach the picker while its host is being checked; the time form's Save button, bound to
   * `form.invalid`, is checked before the rows the picker sits in, so a validity flip there would be an
   * ExpressionChanged error in dev and a stale Save button in prod. Queued once, so `plans`, `status` and
   * `requirePicks` changing together cost one check, made once all three are in.
   */
  private syncAfterInputs(): void {
    if (this.syncQueued) {
      return;
    }
    this.syncQueued = true;
    Promise.resolve().then(() => {
      this.syncQueued = false;
      // Sections that are still loading, or failed to, say nothing about which picks are real: dropping
      // the picks of a prefill against an empty list would wipe an edit before the nurse saw it.
      if (this.status === 'ready') {
        this.emitIfChanged();
      }
      this.onValidatorChange();
    });
  }

  private emitIfChanged(): void {
    const value = this.value;
    const json = JSON.stringify(value);
    if (json !== this.lastValueJson) {
      this.lastValueJson = json;
      this.onChange(value);
    }
  }

  private goalKey(goal: CarePlanPickerGoal): string {
    return this.itemKey(goal.problemId, goal.goalId);
  }

  private itemKey(problemId: string, goalId: string): string {
    return `${problemId}/${goalId}`;
  }
}
